import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { CandidateEntityConstant } from "@workspace/candidate/src/constants/candidateConstant";
import { putCandidate } from "@workspace/candidate/src/store/candidate/action";
import { Formik, Form, Field } from "formik";
import { ObjectHelper } from "@workspace/common";
import * as Yup from "yup";
import SelfIntroMedia from "../SelfIntroMedia/SelfIntroMedia";
import CreatableSelect from "react-select/creatable";

export const CandidateDetailsSection = ({ id, candidate, publicView }) => {
  const dispatch = useDispatch();
  const [candidateData, setCandidateData] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [validationSchema, setValidationSchema] = useState({});
  // For Creatable Select
  const [inputValue, setInputValue] = useState("");
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);

  // Edit Mode based on Field
  const [editMode, setEditMode] = useState({
    firstName: false,
    lastName: false,
    currentPositionTitle: false,
    profileSummary: false,
    phone: false,
    email: false,
    candidateNationality: false,
  });

  // Toggle edit mode for a specific field
  const toggleEditMode = (field) => {
    setEditMode((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Store Submission Data
  useEffect(() => {
    if (candidate) {
      setCandidateData(candidate?.candidateSubmissionData);
    }
  }, [candidate]);

  // Set Initial Values and Validation Schema
  useEffect(() => {
    if (candidateData) {
      const initialValues = Object.keys(candidateData).reduce((acc, key) => {
        acc[key] = candidateData[key] ?? "";
        return acc;
      }, {});

      const validationSchema = Yup.object(
        Object.keys(candidateData).reduce((acc, key) => {
          const value = candidateData[key];

          if (typeof value === "string") {
            if (key.includes("email")) {
              acc[key] = Yup.string().email("Invalid email format");
            } else if (key.includes("phone")) {
              acc[key] = Yup.string().matches(
                /^\d+$/,
                "Must be a valid phone number"
              );
            } else {
              acc[key] = Yup.string().required(`${key} is required`);
            }
          } else if (typeof value === "number") {
            acc[key] = Yup.number().required(`${key} is required`);
          } else if (Array.isArray(value)) {
            acc[key] = Yup.array().of(Yup.string());
          } else {
            acc[key] = Yup.mixed();
          }

          return acc;
        }, {})
      );

      setInitialValues(initialValues);
      setValidationSchema(validationSchema);
    }
  }, [candidateData]);

  // Creatable Components
  const creatableComponents = {
    DropdownIndicator: null,
  };

  // Set Options for Creatable Select
  useEffect(() => {
    if (candidateData?.primarySkill) {
      const options = candidateData?.primarySkill?.split(",").map((skill) => ({
        value: skill,
        label: skill,
      }));
      setDefaultOptions(options);
      setSelectedOptions(options);
    }
  }, [candidateData]);

  // Styling for Creatable Select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "1px solid #E7E9EB",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(50, 86, 237, 0.2)" : "none", // Focus shadow
      "&:hover": {
        border: "1px solid #E7E9EB",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#3256ED",
      borderRadius: "12px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#fff",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#fff",
      "&:hover": {
        backgroundColor: "#3256ED",
        color: "#fff",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3256ED"
        : state.isFocused
        ? "#E7E9EB"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#E7E9EB",
      },
    }),
  };

  // Key Actions for Creatable Select
  const handleKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case "Enter":
      case "Tab":
        setSelectedOptions((prev) => [
          ...prev,
          { value: inputValue, label: inputValue },
        ]);
        setInputValue("");
        event.preventDefault();
    }
  };

  // Save Candidate Details
  const handleSubmit = (values) => {
    const primarySkills = selectedOptions
      .map((option) => option.value)
      .join(",");
    const updatedValues = { ...values, primarySkill: primarySkills };
    const savedCandidateValues = {
      ...updatedValues,
      formData: JSON.stringify(updatedValues),
      formId: parseInt(candidate?.formId),
    };

    const formData = ObjectHelper.convertObjectToFormData(savedCandidateValues);

    dispatch(
      putCandidate({
        entity: CandidateEntityConstant.CANDIDATE_BASIC_INFO,
        id: id,
        newData: formData,
        config: {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      })
    );
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        validateOnChange={false}
      >
        {({ values }) => {
          return (
            <Form className="col-8">
              <div>
                <div className="d-flex flex-column gap-1 mb-3">
                  <div>
                    {editMode?.firstName || editMode?.lastName ? (
                      <div className="d-flex flex-row align-items-end gap-2 my-2">
                        <div>
                          <span className="text-muted">First Name</span>
                          <Field
                            name="firstName"
                            placeholder="Enter First Name"
                            className="form-control"
                            style={{ border: "1px solid #E7E9EB" }}
                          />
                        </div>
                        <div>
                          <span className="text-muted">Last Name</span>
                          <Field
                            name="lastName"
                            placeholder="Enter Last Name"
                            className="form-control"
                            style={{ border: "1px solid #E7E9EB" }}
                          />
                        </div>
                        <div className="d-flex flex-column">
                          <div
                            className="cursor-pointer"
                            style={{ color: "#10B967" }}
                            onClick={() => {
                              handleSubmit(values);
                              toggleEditMode("firstName");
                              toggleEditMode("lastName");
                            }}
                          >
                            <span className="mdi mdi-check me-2"></span>
                            <span>Save</span>
                          </div>
                          <div
                            className="cursor-pointer"
                            style={{ color: "#C71010" }}
                            onClick={() => {
                              toggleEditMode("firstName");
                              toggleEditMode("lastName");
                            }}
                          >
                            <span className="mdi mdi-close me-2"></span>
                            <span>Cancel</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-row align-items-center gap-1 h2"
                        style={{ color: "#3256ED" }}
                      >
                        <span style={{ color: "#495057" }}>I'm</span>
                        <span>{candidateData?.firstName}</span>
                        <span>{candidateData?.lastName},</span>
                        <div
                          style={{
                            backgroundColor: "#E7E9EB",
                            color: "#000",
                            width: "22px",
                            height: "22px",
                            visibility: publicView ? "hidden" : "visible",
                          }}
                          className="cursor-pointer rounded-circle d-flex align-items-center justify-content-center"
                          onClick={() => {
                            toggleEditMode("firstName");
                            toggleEditMode("lastName");
                          }}
                        >
                          <span
                            style={{ fontSize: "11px" }}
                            className="mdi mdi-pencil"
                          ></span>
                        </div>
                      </div>
                    )}
                  </div>

                  {editMode?.currentPositionTitle ? (
                    <div className="d-flex flex-row align-items-end gap-2 my-2">
                      <div>
                        <span className="text-muted">Current Position</span>
                        <Field
                          name="currentPositionTitle"
                          placeholder="Enter Current Position"
                          className="form-control"
                          style={{ border: "1px solid #E7E9EB" }}
                        />
                      </div>
                      <div className="d-flex flex-column">
                        <div
                          className="cursor-pointer"
                          style={{ color: "#10B967" }}
                          onClick={() => {
                            handleSubmit(values);
                            toggleEditMode("currentPositionTitle");
                          }}
                        >
                          <span className="mdi mdi-check me-2"></span>
                          <span>Save</span>
                        </div>
                        <div
                          className="cursor-pointer"
                          style={{ color: "#C71010" }}
                          onClick={() => toggleEditMode("currentPositionTitle")}
                        >
                          <span className="mdi mdi-close me-2"></span>
                          <span>Cancel</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex flex-row align-items-center gap-2">
                      <div>
                        <span className="h2">
                          {candidateData?.currentPositionTitle}
                        </span>
                      </div>
                      <div
                        style={{
                          backgroundColor: "#E7E9EB",
                          color: "#000",
                          width: "22px",
                          height: "22px",
                          visibility: publicView ? "hidden" : "visible",
                        }}
                        className="cursor-pointer rounded-circle d-flex align-items-center justify-content-center"
                        onClick={() => toggleEditMode("currentPositionTitle")}
                      >
                        <span
                          style={{ fontSize: "11px" }}
                          className="mdi mdi-pencil"
                        ></span>
                      </div>
                    </div>
                  )}

                  <div>
                    <span className="h4 me-1">
                      {candidateData?.totalExperience} Years
                    </span>
                    <span className="h4 fw-light">Experience</span>
                  </div>
                </div>

                <div
                  className="mb-3"
                  style={{
                    border: publicView ? "none" : "1px solid #E7E9EB",
                    borderRadius: "10px",
                    padding: publicView ? "0px" : "10px",
                    width: "100%",
                  }}
                >
                  {editMode?.profileSummary ? (
                    <div>
                      <span className="text-muted">Profile Summary</span>
                      <Field
                        as="textarea"
                        rows="3"
                        name="profileSummary"
                        placeholder="Write a concise bio in up to 3 lines or a maximum of 25 words. Highlight your expertise, achievements, or interests in a way that effectively represents you."
                        className="form-control mb-3"
                        style={{ border: "1px solid #E7E9EB" }}
                      />
                      <div className="d-flex flex-row gap-3">
                        <div
                          className="cursor-pointer"
                          style={{ color: "#10B967" }}
                          onClick={() => {
                            handleSubmit(values);
                            toggleEditMode("profileSummary");
                          }}
                        >
                          <span className="mdi mdi-check me-2"></span>
                          <span>Save</span>
                        </div>
                        <div
                          className="cursor-pointer"
                          style={{ color: "#C71010" }}
                          onClick={() => toggleEditMode("profileSummary")}
                        >
                          <span className="mdi mdi-close me-2"></span>
                          <span>Cancel</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="d-flex flex-row justify-content-between align-items-center">
                        <div className="d-flex flex-row align-items-start gap-2">
                          <span className="fw-semibold mb-2">
                            Profile Summary
                          </span>
                          <div
                            style={{
                              backgroundColor: "#E7E9EB",
                              color: "#000",
                              width: "22px",
                              height: "22px",
                              visibility: publicView ? "hidden" : "visible",
                            }}
                            className="cursor-pointer rounded-circle d-flex align-items-center justify-content-center"
                            onClick={() => toggleEditMode("profileSummary")}
                          >
                            <span
                              style={{ fontSize: "11px" }}
                              className="mdi mdi-pencil"
                            ></span>
                          </div>
                        </div>
                      </div>
                      <p style={{ textAlign: "justify", textWrap: "wrap" }}>
                        {candidateData?.profileSummary
                          ? candidateData?.profileSummary
                          : "Write a concise bio in up to 3 lines or a maximum of 25 words. Highlight your expertise, achievements, or interests in a way that effectively represents you."}
                      </p>
                    </>
                  )}
                </div>

                <div className="d-flex flex-column gap-2 mb-3">
                  {/* Phone */}
                  <div className="d-flex flex-row align-items-center gap-2">
                    <div
                      className="d-flex justify-content-center align-items-center rounded-circle"
                      style={{
                        width: "25px",
                        height: "25px",
                        backgroundColor: "#E7E9EB",
                        color: "#141313",
                      }}
                    >
                      <span className="mdi mdi-phone"></span>
                    </div>
                    {editMode?.phone || editMode?.phoneCode ? (
                      <div className="d-flex flex-row align-items-end gap-2">
                        <div>
                          <span className="text-muted">Phone Extension</span>
                          <Field
                            name="phoneCode"
                            placeholder="Enter Phone Code"
                            style={{ border: "1px solid #E7E9EB" }}
                            className="form-control"
                          />
                        </div>
                        <div>
                          <span className="text-muted">Contact Number</span>
                          <Field
                            name="phone"
                            placeholder="Enter Contact Number"
                            style={{ border: "1px solid #E7E9EB" }}
                            className="form-control"
                          />
                        </div>
                        <div className="d-flex flex-column">
                          <div
                            className="cursor-pointer"
                            style={{ color: "#10B967" }}
                            onClick={() => {
                              handleSubmit(values);
                              toggleEditMode("phoneCode");
                              toggleEditMode("phone");
                            }}
                          >
                            <span className="mdi mdi-check me-2"></span>
                            <span>Save</span>
                          </div>
                          <div
                            className="cursor-pointer"
                            style={{ color: "#C71010" }}
                            onClick={() => {
                              toggleEditMode("phone");
                              toggleEditMode("phoneCode");
                            }}
                          >
                            <span className="mdi mdi-close me-2"></span>
                            <span>Cancel</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-row align-items-center gap-2">
                        <span>
                          {candidateData?.phoneCode} {candidateData?.phone}
                        </span>
                        <div
                          style={{
                            backgroundColor: "#E7E9EB",
                            color: "#000",
                            width: "22px",
                            height: "22px",
                            visibility: publicView ? "hidden" : "visible",
                          }}
                          className="cursor-pointer rounded-circle d-flex align-items-center justify-content-center"
                          onClick={() => {
                            toggleEditMode("phone");
                            toggleEditMode("phoneCode");
                          }}
                        >
                          <span
                            style={{ fontSize: "11px" }}
                            className="mdi mdi-pencil"
                          ></span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div className="d-flex flex-row align-items-center gap-2">
                    <div
                      className="d-flex justify-content-center align-items-center rounded-circle"
                      style={{
                        width: "25px",
                        height: "25px",
                        backgroundColor: "#E7E9EB",
                        color: "#141313",
                      }}
                    >
                      <span className="mdi mdi-mail"></span>
                    </div>
                    {editMode?.email ? (
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-row align-items-end gap-2">
                          <div>
                            <span className="text-muted">Email</span>
                            <Field
                              name="email"
                              placeholder="Enter Email"
                              className="form-control"
                              style={{ border: "1px solid #E7E9EB" }}
                            />
                          </div>
                          <div className="d-flex flex-column">
                            <div
                              className="cursor-pointer"
                              style={{ color: "#10B967" }}
                              onClick={() => {
                                handleSubmit(values);
                                toggleEditMode("email");
                              }}
                            >
                              <span className="mdi mdi-check me-2"></span>
                              <span>Save</span>
                            </div>
                            <div
                              className="cursor-pointer"
                              style={{ color: "#C71010" }}
                              onClick={() => toggleEditMode("email")}
                            >
                              <span className="mdi mdi-close me-2"></span>
                              <span>Cancel</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-row align-items-center gap-2">
                        <span>{candidateData?.email}</span>
                        <div
                          style={{
                            backgroundColor: "#E7E9EB",
                            color: "#000",
                            width: "22px",
                            height: "22px",
                            visibility: publicView ? "hidden" : "visible",
                          }}
                          className="cursor-pointer rounded-circle d-flex align-items-center justify-content-center"
                          onClick={() => toggleEditMode("email")}
                        >
                          <span
                            style={{ fontSize: "11px" }}
                            className="mdi mdi-pencil"
                          ></span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  <div className="d-flex flex-row align-items-center gap-2">
                    <div
                      className="d-flex justify-content-center align-items-center rounded-circle"
                      style={{
                        width: "25px",
                        height: "25px",
                        backgroundColor: "#E7E9EB",
                        color: "#141313",
                      }}
                    >
                      <span className="mdi mdi-map-marker"></span>
                    </div>
                    {editMode?.candidateNationality ? (
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-row align-items-end gap-2">
                          <div>
                            <span className="text-muted">Nationality</span>
                            <Field
                              name="candidateNationality"
                              placeholder="Enter Nationality"
                              className="form-control"
                              style={{ border: "1px solid #E7E9EB" }}
                            />
                          </div>
                          <div className="d-flex flex-column">
                            <div
                              className="cursor-pointer"
                              style={{ color: "#10B967" }}
                              onClick={() => {
                                handleSubmit(values);
                                toggleEditMode("candidateNationality");
                              }}
                            >
                              <span className="mdi mdi-check me-2"></span>
                              <span>Save</span>
                            </div>
                            <div
                              className="cursor-pointer"
                              style={{ color: "#C71010" }}
                              onClick={() =>
                                toggleEditMode("candidateNationality")
                              }
                            >
                              <span className="mdi mdi-close me-2"></span>
                              <span>Cancel</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-row align-items-center gap-2">
                        <span>{candidateData?.candidateNationality}</span>
                        <div
                          style={{
                            backgroundColor: "#E7E9EB",
                            color: "#000",
                            width: "22px",
                            height: "22px",
                            visibility: publicView ? "hidden" : "visible",
                          }}
                          className="cursor-pointer rounded-circle d-flex align-items-center justify-content-center"
                          onClick={() => toggleEditMode("candidateNationality")}
                        >
                          <span
                            style={{ fontSize: "11px" }}
                            className="mdi mdi-pencil"
                          ></span>
                        </div>
                      </div>
                    )}
                    <div className="d-flex flex-column mb-3">
                      {/* Nationality - Edit */}
                    </div>
                  </div>
                </div>

                <div>
                  {editMode.primarySkill ? (
                    <div>
                      <span className="text-muted">My Expertise</span>
                      <div>
                        <CreatableSelect
                          isClearable
                          isMulti
                          onKeyDown={handleKeyDown}
                          menuIsOpen={false}
                          value={selectedOptions}
                          inputValue={inputValue}
                          onInputChange={(value) => setInputValue(value)}
                          onChange={(value) => setSelectedOptions(value)}
                          components={creatableComponents}
                          placeholder="Add Skills"
                          styles={customStyles}
                        />
                      </div>
                      <div className="d-flex flex-row gap-2">
                        <div
                          className="cursor-pointer"
                          style={{ color: "#10B967" }}
                          onClick={() => {
                            handleSubmit(values);
                            toggleEditMode("primarySkill");
                          }}
                        >
                          <span className="mdi mdi-check me-2"></span>
                          <span>Save</span>
                        </div>
                        <div
                          className="cursor-pointer"
                          style={{ color: "#C71010" }}
                          onClick={() => {
                            toggleEditMode("primarySkill");
                          }}
                        >
                          <span className="mdi mdi-close me-2"></span>
                          <span>Cancel</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      <span className="fw-semibold">My Expertise</span>
                      <div className="d-flex flex-row gap-2">
                        {candidateData?.primarySkill
                          ?.split(",")
                          .map((skill, index) => (
                            <span
                              key={index}
                              style={{
                                backgroundColor: "#000",
                                color: "#fff",
                                padding: "4px 7px",
                                borderRadius: "15px",
                                fontSize: "11px",
                              }}
                            >
                              {skill}
                            </span>
                          ))}
                        <span
                          style={{
                            backgroundColor: "#CDCDCD",
                            color: "#000",
                            padding: "4px 7px",
                            borderRadius: "15px",
                            fontSize: "11px",
                            cursor: "pointer",
                            visibility: publicView ? "hidden" : "visible",
                          }}
                          onClick={() => toggleEditMode("primarySkill")}
                        >
                          Add New
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      <div className="d-flex justify-content-end col-4">
        <SelfIntroMedia
          candidateId={id}
          editable={true}
          publicView={publicView}
        />
      </div>
    </>
  );
};
